import { computed } from "vue";
import { CostTypeEnum, type AggregateExternalCosts } from "@simbelapp/order-sdk";
import { useCurrency } from "~/composables/currency/useCurrency";
import type { IIndexHeader } from "~/utils/interfaces/table-interfaces";

const SALARY_COST_HEADERS: IIndexHeader[] = [
  { name: "Participant", visible: true, maxWidthFr: 1 },
  {
    name: "Heures",
    visible: true,
    minWidthPx: 140,
    align: "right" as const,
    tooltip: "La durée théorique, ou les heures de présence de chaque participant si la formation est terminée.",
  },
  {
    name: "Coût / h",
    visible: true,
    minWidthPx: 140,
    align: "right" as const,
    tooltip: "Coût horaire chargé du participant.",
  },
  { name: "Total", visible: true, minWidthPx: 140, align: "right" as const },
];

const INTERNAL_TRAINER_COST_HEADERS: IIndexHeader[] = [
  { name: "Formateur", visible: true, maxWidthFr: 1 },
  {
    name: "Heures",
    visible: true,
    minWidthPx: 140,
    align: "right" as const,
    tooltip: "La somme des durées des étapes animées par le formateur.",
  },
  {
    name: "Coût / h",
    visible: true,
    minWidthPx: 140,
    align: "right" as const,
    tooltip: "Coût horaire chargé du formateur.",
  },
  { name: "Total", visible: true, minWidthPx: 140, align: "right" as const },
];

const COST_TYPE_LABELS: Record<CostTypeEnum, string> = {
  [CostTypeEnum.Other]: "Autre",
  [CostTypeEnum.Salary]: "Salaire",
  [CostTypeEnum.TrainerSalary]: "Coût salaire formateur",
  [CostTypeEnum.Catering]: "Restauration",
  [CostTypeEnum.Accommodation]: "Hébergement",
  [CostTypeEnum.Equipment]: "Matériel",
  [CostTypeEnum.Trainer]: "Coût formateur",
  [CostTypeEnum.Transport]: "Transport",
  [CostTypeEnum.Venue]: "Lieu",
};

// Attendee/trainer salaries are auto-derived from hourly rates, never entered as an ancillary
// cost — they are excluded from every "frais annexes" list and total.
const SALARY_COST_TYPES: CostTypeEnum[] = [CostTypeEnum.Salary, CostTypeEnum.TrainerSalary];

const EXTERNAL_COST_TYPES = Object.values(CostTypeEnum).filter((item) => !SALARY_COST_TYPES.includes(item));

/**
 * Keeps only the true ancillary costs of a raw `externalCosts` aggregate, which groups every cost
 * type together — salary buckets included. Mirrors the backend's `otherCosts`, so recaps derived
 * client-side stay consistent with the Costs tab.
 */
export function filterAncillaryExternalCosts(costs?: AggregateExternalCosts[] | null): AggregateExternalCosts[] {
  return costs?.filter((cost) => !SALARY_COST_TYPES.includes(cost.costType)) ?? [];
}

export function sumAncillaryExternalCosts(costs?: AggregateExternalCosts[] | null): number {
  return filterAncillaryExternalCosts(costs).reduce((acc, cost) => acc + Number(cost.priceExclTax ?? 0), 0);
}

export const ALL_PARTICIPANTS_LABEL = "Tous les participants";

export interface IAllParticipantsOption {
  displayValue: string;
  id: string;
  firstname: null;
  lastname: null;
  userId: string;
  email: null;
}

export function buildAllParticipantsOption(id: string): IAllParticipantsOption {
  return { displayValue: ALL_PARTICIPANTS_LABEL, id, firstname: null, lastname: null, userId: "", email: null };
}

export const useExternalCost = () => {
  const { currencySymbol } = useCurrency();

  // Computed (not a plain array): the "Coût" header embeds the currency symbol, which is
  // reactive to the company's currency — a static array would go stale vs the reactive price cells.
  const externalCostHeaders = computed<IIndexHeader[]>(() => [
    { name: "Type", visible: true, minWidthPx: 110, maxWidthFr: 1 },
    { name: "Pour qui ?", visible: true, minWidthPx: 109, maxWidthFr: 1, sort: true, key: "lastname" },
    { name: `Coût ${currencySymbol.value}`, visible: true, minWidthPx: 110, maxWidthFr: 1, align: "right" as const },
  ]);
  const externalCostEditHeaders = computed<IIndexHeader[]>(() => [
    ...externalCostHeaders.value,
    { name: "", visible: true },
  ]);

  function getCostType(costType: CostTypeEnum | string): string {
    return COST_TYPE_LABELS[costType as CostTypeEnum] ?? "";
  }

  function hourlyTotal(rate: number | null, hours: number | null): number {
    return Number(rate ?? 0) * Number(hours ?? 0);
  }

  return {
    getCostType,
    hourlyTotal,
    buildAllParticipantsOption,
    allParticipantsLabel: ALL_PARTICIPANTS_LABEL,
    externalCostHeaders,
    externalCostEditHeaders,
    externalCostTypes: EXTERNAL_COST_TYPES,
    salaryCostHeaders: SALARY_COST_HEADERS,
    internalTrainerCostHeaders: INTERNAL_TRAINER_COST_HEADERS,
  };
};
